import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/client";
import Box from "@mui/material/Box";
import { Select } from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import { titleCase } from "title-case";
import { MERCHANT_NAME_SEARCH } from "../../graphql/card";
import { MIN_SEARCH_TERM_LENGTH } from "./Constants";
import MerchantSearchType from "enums/MerchantSearchType";
import { useSelectSearch } from "hooks/useSelectSearch";

export interface Merchant {
  id: string;
  name: string;
  logo: string;
  cachedLogo: string;
  url: string;
}

/** A bare domain as a merchant: what a typed domain becomes, and how a saved website shows. */
export const merchantForDomain = (domain: string): Merchant => ({
  id: "",
  name: domain,
  logo: "",
  cachedLogo: "",
  url: domain,
});

// A domain-only merchant has no id, and the Select needs one to show it as the value.
const optionId = (merchant: Merchant) =>
  merchant.id || `domain:${merchant.url}`;

const MerchantLogo = ({ src }: { src: string }) => (
  <Box
    component="img"
    src={src}
    alt=""
    sx={{ width: 24, height: 24, objectFit: "contain" }}
    onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
      event.currentTarget.style.display = "none";
    }}
  />
);

interface MerchantSearchAutoCompleteProps {
  title?: string;
  placeholder?: string;
  merchant?: Merchant | null;
  onSelectMerchant: (merchant: Merchant | null) => void;
  fullWidth?: boolean;
  /** Offers the typed text as a merchant of its own, for a domain the search does not know. */
  allowUseSearchValue?: boolean;
  helperText?: React.ReactNode;
  errorText?: React.ReactNode;
  handleBlur?: React.FocusEventHandler<HTMLInputElement>;
  type?: MerchantSearchType;
  required?: boolean;
  disabled?: boolean;
}

/**
 * The merchant picker. It searches only once three characters are typed, and
 * `highlight` carries the term the results came back with. A caller that
 * passes no `merchant` gets a field that empties after each pick, which is how
 * the card policy form adds merchants one at a time.
 */
const MerchantSearchAutoComplete: React.FC<MerchantSearchAutoCompleteProps> = ({
  title = "",
  placeholder,
  merchant = null,
  onSelectMerchant,
  fullWidth = true,
  allowUseSearchValue = false,
  helperText,
  errorText,
  handleBlur,
  type = MerchantSearchType.name,
  required = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  // Options carry only `{ id, label }`; callers want the merchant back.
  const seenRef = useRef(new Map<string, Merchant>());

  const fetchPage = useCallback(
    async (query: string) => {
      if (query.length < MIN_SEARCH_TERM_LENGTH) return { options: [] };
      const { data } = await client.query({
        query: MERCHANT_NAME_SEARCH,
        variables: { searchValue: query, searchType: type },
        fetchPolicy: "network-only",
      });
      // Copied field by field: callers send the merchant on to mutations, which reject __typename.
      // The schema's name is nullable, and a nameless result has nothing to show, so it is left out.
      const merchants: Merchant[] = (data?.merchantNameSearch ?? [])
        .filter(({ name }: { name?: string | null }) => Boolean(name))
        .map(({ id, name, logo, cachedLogo, url }: Merchant) => ({
          id,
          name,
          logo,
          cachedLogo,
          url,
        }));
      merchants.forEach((m) => seenRef.current.set(optionId(m), m));
      return {
        options: merchants.map((m) => ({
          id: optionId(m),
          label: titleCase(m.name.toLowerCase()),
          leading: m.logo ? <MerchantLogo src={m.logo} /> : undefined,
        })),
      };
    },
    [client, type],
  );

  const seed = useMemo<SelectOption | null>(
    () => (merchant ? { id: optionId(merchant), label: merchant.name } : null),
    [merchant],
  );

  const search = useSelectSearch<SelectOption>({ fetchPage, seed });
  // The live input, for the typed-domain row; `search.query` lags it by a request, or stays put if one fails.
  const [typedTerm, setTypedTerm] = useState("");

  const clearTerm = () => {
    setTypedTerm("");
    search.reset();
  };

  // The DS Select keeps a picked label in a focused input while `value` stays empty, so a field with no `merchant` remounts to clear it.
  const [picks, setPicks] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (picks > 0) inputRef.current?.focus();
  }, [picks]);

  const select = (next: Merchant | null) => {
    // Drop the term, so reopening the field does not show the last search's rows.
    clearTerm();
    if (next && !merchant) setPicks((count) => count + 1);
    onSelectMerchant(next);
  };

  const onChange = (id: string | null) => {
    if (!id) {
      select(null);
      return;
    }
    select(seenRef.current.get(id) ?? merchant);
  };

  const canUseTypedTerm =
    allowUseSearchValue && typedTerm.length >= MIN_SEARCH_TERM_LENGTH;

  return (
    <Select
      key={picks}
      ref={inputRef}
      label={title || undefined}
      aria-label={title ? undefined : t("Merchant")}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      helperText={helperText}
      errorText={errorText || undefined}
      options={search.options}
      loading={search.loading}
      highlight={search.query}
      onInputChange={(query) => {
        setTypedTerm(query);
        search.onInputChange(query);
      }}
      onScrollEnd={search.onScrollEnd}
      value={seed?.id ?? null}
      onChange={onChange}
      onBlur={(event) => {
        clearTerm();
        handleBlur?.(event);
      }}
      bottomAction={
        canUseTypedTerm
          ? {
              label: t('Use "{{term}}"', { term: typedTerm }),
              onClick: () => select(merchantForDomain(typedTerm)),
            }
          : undefined
      }
    />
  );
};

export default MerchantSearchAutoComplete;
